import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  MenuItem,
  Chip,
  Typography,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import SearchIcon from '@mui/icons-material/Search';
import Scrollbar from '../../../custom-scroll/Scrollbar';
import {
  MESSAGE_STATUS_LABELS,
  MESSAGE_STATUS_COLORS,
} from '../../../apps/campaigns/constants';


export const CampaignMessagesTable = ({
  messages = [],
  loading = false,
  phoneSearch = '',
  statusFilter = 'todos',
  onPhoneSearchChange,
  onStatusFilterChange,
  page = 1,
  pageSize = 10,
  total = 0,
  onPageChange,
  onPageSizeChange,
}) => {

  const getStatusColor = (status) => {
    return MESSAGE_STATUS_COLORS[status] || 'default';
  };


  const formatDate = (dateString) => {
    if (!dateString) return '-';

    try {
      const date = new Date(dateString.replace(' ', 'T'));
      
      const options = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      };
      
      return date.toLocaleString('es-ES', options);
    } catch (error) {
      return dateString;
    }
  };

 
  const formatPhone = (phone) => {
    if (!phone) return '-';

    if (phone.length > 10) {
      const countryCode = phone.slice(0, phone.length - 10);
      const areaCode = phone.slice(-10, -8);
      const firstPart = phone.slice(-8, -4);
      const secondPart = phone.slice(-4);
      return `+${countryCode} ${areaCode} ${firstPart} ${secondPart}`;
    }

    return phone;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: { xs: 'flex-start', sm: 'space-between' },
          alignItems: { xs: 'stretch', sm: 'center' },
          mb: 2,
          gap: 2,
        }}
      >
        <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
          <TextField
            fullWidth
            placeholder="Buscar por teléfono"
            value={phoneSearch}
            onChange={(e) => onPhoneSearchChange(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box sx={{ width: { xs: '100%', sm: 250 } }}>
          <TextField
            select
            fullWidth
            label="Filtrar por estado"
            value={statusFilter}
            onChange={(e) => onStatusFilterChange(e.target.value)}
            size="small"
          >
            <MenuItem value="todos">Todos los estados</MenuItem>
            <MenuItem value="PENDING">Pendiente</MenuItem>
            <MenuItem value="SENT">Enviado</MenuItem>
            <MenuItem value="DELIVERED">Entregado</MenuItem>
            <MenuItem value="FAILED">Fallido</MenuItem>
            <MenuItem value="READ">Leído</MenuItem>
          </TextField>
        </Box>
      </Box>

      <Paper
        elevation={2}
        sx={{
          width: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          flex: '1 1 auto',
          minHeight: 0,
        }}
      >
        <TableContainer
          component={Scrollbar}
          sx={{
            flex: '1 1 auto',
            minHeight: 0,
          }}
        >
          <Table stickyHeader sx={{ minWidth: 650 }} aria-label="tabla de mensajes">
            <TableHead>
              <TableRow
                sx={{
                  '& .MuiTableCell-head': {
                    backgroundColor: grey[100],
                  },
                }}
              >
                <TableCell>
                  <Box sx={{ fontWeight: 'bold' }}>ID</Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ fontWeight: 'bold' }}>Teléfono</Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ fontWeight: 'bold' }}>Fecha de Envío</Box>
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ fontWeight: 'bold' }}>Estado</Box>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Box sx={{ color: 'text.secondary' }}>Cargando mensajes...</Box>
                  </TableCell>
                </TableRow>
              ) : messages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Box sx={{ color: 'text.secondary' }}>
                      {phoneSearch || statusFilter !== 'todos'
                        ? 'No se encontraron mensajes con los filtros aplicados'
                        : 'No hay mensajes disponibles para esta campaña'}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                messages.map((message) => (
                  <TableRow
                    key={message.id}
                    sx={{
                      '&:hover': {
                        backgroundColor: (theme) => theme.palette.action.hover,
                      },
                      '&:last-child td, &:last-child th': { border: 0 },
                    }}
                  >
                    <TableCell component="th" scope="row">
                      {message.id}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" >
                        {formatPhone(message.phone_destination)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" >
                        {formatDate(message.date_sent)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={MESSAGE_STATUS_LABELS[message.real_status?.toUpperCase()] || message.real_status}
                        color={getStatusColor(message.real_status?.toUpperCase())}
                        size="small"
                        sx={{
                          fontWeight: 'medium',
                          minWidth: 90,
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page - 1}
          onPageChange={(e, newPage) => onPageChange(newPage + 1)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            const newSize = parseInt(e.target.value, 10);
            onPageSizeChange(newSize);
          }}
          sx={{
            flex: '0 0 auto',
            overflow: 'hidden',
          }}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
          labelRowsPerPage="Filas por página:"
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>
    </Box>
  );
};

CampaignMessagesTable.propTypes = {
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      phone_destination: PropTypes.string.isRequired,
      date_sent: PropTypes.string,
      real_status: PropTypes.string,
    })
  ),
  loading: PropTypes.bool,
  phoneSearch: PropTypes.string,
  statusFilter: PropTypes.string,
  onPhoneSearchChange: PropTypes.func.isRequired,
  onStatusFilterChange: PropTypes.func.isRequired,
  page: PropTypes.number,
  pageSize: PropTypes.number,
  total: PropTypes.number,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
};